import React, { Fragment, PureComponent } from 'react'
import { observer } from 'mobx-react'
import { Link } from 'react-router-dom'

import Button from '@UniformComponents/Button'
import ConfirmationModal from '../../../../UniformComponents/ConfirmationModal'
import DataTable from '../../../DataTable'
import CustomTagGroup from './CustomTagGroup'
import CustomTagGroupEdit from './CustomTagGroupEdit'

@observer
class CustomTagSearch extends PureComponent {
    tableColumns = []

    constructor(props) {
        super(props)
        this.state = {
            view: 'tags',
            searchStr: '',
            deletingGroup: null,
            deletingTag: null,
        }
    }

    componentDidMount() {
        const { store } = this.props
        this.setColumns()
        store.initSearch()
    }

    setColumns = () => {
        const { store } = this.props
        const levels = store.getLevels()

        this.tableColumns = [
            {
                name: 'id',
                key: 'id',
            },
            {
                name: 'Name',
                key: 'name',
            },
            {
                name: 'Group',
                key: 'group',
                dataTransform: (group) => group?.name || '',
            },
            {
                name: 'Level',
                key: 'oid',
                dataTransform: (oid) => (
                    levels
                        .filter(({ value }) => value === oid)
                        .map(({ label }) => label)
                ),
            },
            {
                name: 'Client',
                key: 'clientId',
            },
            {
                name: 'Industry',
                key: 'industry',
            },
            {
                name: 'Event type',
                key: 'productType',
            },
            {
                name: 'Survey type',
                key: 'surveyType',
            },
            {
                name: 'Respondent type',
                key: 'respondentType',
            },
            {
                name: 'Options',
                key: 'options',
            },
            {
                name: '',
                key: 'id',
                dataTransform: (id) => (
                    <Link
                        className="fa-light fa-pen-to-square rowEditIcon"
                        to={`/intranet/customTag/${id}`}
                        onClick={(e) => e.stopPropagation()}
                    />
                ),
            },
            {
                name: '',
                key: 'id',
                dataTransform: (id, row) => (
                    <i
                        className="fa-light fa-trash-can rowDeleteIcon"
                        onClick={(e) => {
                            e.stopPropagation()
                            this.openDeleteTagModal(row)
                        }}
                    />
                ),
            },
        ]
    }

    showTags = () => this.setState({ view: 'tags' })

    showGroups = () => {
        const { store } = this.props
        this.setState({ view: 'groups' })
        store.fetchGroups()
    }

    onSearchChange = (e) => this.setState({ searchStr: e.target.value })

    goToNew = () => {
        const { history } = this.props
        history.push('/intranet/customTag/0')
    }

    openDeleteGroupModal = (group) => this.setState({ deletingGroup: group })

    closeDeleteGroupModal = () => this.setState({ deletingGroup: null })

    onDeleteGroup = async () => {
        const { store } = this.props
        const { deletingGroup } = this.state
        await store.deleteGroup(deletingGroup.id)
        this.closeDeleteGroupModal()
    }

    deleteGroupContent = (group) => (
        `Are you sure you want to delete the group "${group.name}"?
        This will only delete the group, not the custom tags.
        `
    )

    openDeleteTagModal = (tag) => this.setState({ deletingTag: tag })

    closeDeleteTagModal = () => this.setState({ deletingTag: null })

    onDeleteTag = async () => {
        const { store } = this.props
        const { deletingTag } = this.state
        await store.delete(deletingTag.id)
        this.closeDeleteTagModal()
    }

    deleteTagContent = (tag) => `Are you sure you want to delete the custom tag "${tag.name}"?`

    matchesSearch = (searchStr, ...fields) => {
        if (!searchStr) return true
        const re = new RegExp(searchStr, 'i')
        return fields.some((field) => field !== undefined && field !== null && String(field).match(re))
    }

    filteredTags = () => {
        const { store } = this.props
        const { searchStr } = this.state

        return store.tagList.filter((tag) => this.matchesSearch(
            searchStr,
            tag.id,
            tag.name,
            tag.group?.name,
            tag.clientId,
            tag.industry,
            tag.productType,
            tag.surveyType,
            tag.respondentType,
            tag.options,
        ))
    }

    filteredGroups = () => {
        const { store } = this.props
        const { searchStr } = this.state

        return store.groups?.filter((group) => this.matchesSearch(
            searchStr,
            group.name,
            ...group.tags.map((tag) => tag.name),
        ))
    }

    renderTabs = () => {
        const { view } = this.state

        return (
            <div className="customTagTabs">
                <button
                    type="button"
                    className={view === 'tags' ? 'active' : ''}
                    onClick={this.showTags}
                >
                    <i className="fa-regular fa-tag" />
                    <span>Custom tags</span>
                </button>
                <button
                    type="button"
                    className={view === 'groups' ? 'active' : ''}
                    onClick={this.showGroups}
                >
                    <i className="fa-regular fa-tags" />
                    <span>Groups</span>
                </button>
            </div>
        )
    }

    renderSearchRow = () => {
        const { store } = this.props
        const { view, searchStr } = this.state
        const isGroups = view === 'groups'

        return (
            <div className="customTagSearchRow">
                <div className="customTagSearchField">
                    <input
                        value={searchStr}
                        onChange={this.onSearchChange}
                        placeholder="Search tags or groups"
                    />
                    <i className="fa-solid fa-magnifying-glass" />
                </div>
                <Button
                    type="button"
                    label={isGroups ? 'Add group' : 'Add custom tag'}
                    icon="fa-solid fa-plus"
                    onClick={isGroups ? store.openNewGroupModal : this.goToNew}
                    cssClass="regular secondary customTagAddButton"
                />
            </div>
        )
    }

    renderDataTable = () => {
        const { history } = this.props
        const rows = this.filteredTags()

        const tableArgs = {
            data: rows,
            columns: this.tableColumns,
            rowClick: (e, id) => { history.push(`/intranet/customTag/${id}`) },
            rowId: 'id',
            emptyMessage: 'No records at the moment',
        }

        if (tableArgs.data.length > 0) {
            return (
                <DataTable
                    store={{ filteredList: tableArgs.data }}
                    dataSet={tableArgs.data}
                    columns={tableArgs.columns}
                    rowClick={tableArgs.rowClick}
                    rowId={tableArgs.rowId}
                    inMemory
                />
            )
        }
        return <div>{tableArgs.emptyMessage}</div>
    }

    renderGroupsWarning = () => (
        <div className="adminOnlyWarning">
            <i className="fa-light fa-triangle-exclamation" />
            <span> Only admin tags can be grouped </span>
        </div>
    )

    renderGroupsList = () => {
        const { store } = this.props
        const groups = this.filteredGroups()

        return (
            <div className="customTagGroupsList">
                {groups.map((group) => (
                    <CustomTagGroup
                        key={group.id}
                        store={store}
                        group={group}
                        onEdit={() => store.openEditGroupModal(group)}
                        onDelete={() => this.openDeleteGroupModal(group)}
                    />
                ))}
            </div>
        )
    }

    render() {
        const { store } = this.props
        const { isLoading } = store
        const { view, deletingGroup, deletingTag } = this.state

        if (isLoading) return null

        return (
            <Fragment>
                <div className="customTagSearch">
                    {this.renderTabs()}
                    {view === 'groups' ? this.renderGroupsWarning() : null}
                    {this.renderSearchRow()}
                    {view === 'tags' ? this.renderDataTable() : this.renderGroupsList()}
                </div>

                <CustomTagGroupEdit store={store} />

                <ConfirmationModal
                    show={Boolean(deletingGroup)}
                    onClose={this.closeDeleteGroupModal}
                    title="Delete group?"
                    description={deletingGroup ? this.deleteGroupContent(deletingGroup) : ''}
                    onFalse={this.closeDeleteGroupModal}
                    onFalseLabel="Cancel"
                    onTrue={this.onDeleteGroup}
                    onTrueLabel="Delete"
                    onTrueCss="error"
                />

                <ConfirmationModal
                    show={Boolean(deletingTag)}
                    onClose={this.closeDeleteTagModal}
                    title="Delete custom tag?"
                    description={deletingTag ? this.deleteTagContent(deletingTag) : ''}
                    onFalse={this.closeDeleteTagModal}
                    onFalseLabel="Cancel"
                    onTrue={this.onDeleteTag}
                    onTrueLabel="Delete"
                    onTrueCss="error"
                />
            </Fragment>
        )
    }
}

export default CustomTagSearch
